import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { TextField, Button, Box, Typography, IconButton, Tooltip, Card, Chip, MenuItem } from '@mui/material';
import { Info as InfoIcon, AttachFile as AttachFileIcon, Delete as DeleteIcon, Assignment as TemplateIcon } from '@mui/icons-material';
import { useWebSocket } from './WebSocketContext';
import '../styles/AddCase.css';
import { useNavigate } from 'react-router-dom';
import CreateNewFolderIcon from '@mui/icons-material/CreateNewFolder';
import { useTranslation } from 'react-i18next';
import TemplateSelectionDialog from './TemplateSelectionDialog';
import ManualCreationDialog from './ManualCreationDialog';
import LoadingScreen from './LoadingScreen';
import logo from '../images/logowithname2.png';

// Theme colors to match Sidebar
const primaryColor = '#5757b0';
const primaryColorTransparent = 'rgba(87, 87, 176, 0.8)';
const hoverColor = '#7777d0';
const selectedColor = '#4545a0';

const AddProject = ({ onAddProject }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [status, setStatus] = useState('signup'); // Show form by default
  const [updateMessage, setUpdateMessage] = useState('');
  const [goal, setGoal] = useState('');
  const [typedPlaceholder, setTypedPlaceholder] = useState('');
  const [projectModelProvider, setProjectModelProvider] = useState('');

  const [projectTextModelId, setProjectTextModelId] = useState('');
  const [projectTextModelProvider, setProjectTextModelProvider] = useState('');
  const [projectVisionModelId, setProjectVisionModelId] = useState('');
  const [projectVisionModelProvider, setProjectVisionModelProvider] = useState('');
  const [projectAudioModelId, setProjectAudioModelId] = useState('');
  const [projectAudioModelProvider, setProjectAudioModelProvider] = useState('');
  const [savedModels, setSavedModels] = useState({ text: [], vision: [], audio: [] });
  const [serverVersion, setServerVersion] = useState(null);
  const [pendingCaseId, setPendingCaseId] = useState(null);
  const shouldAwaitCreationUpdatesRef = useRef(false);

  const [attachments, setAttachments] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [manualCreationOpen, setManualCreationOpen] = useState(false);
  const { subscribeToMessages, sendMessage } = useWebSocket();
  const rotatingPlaceholders = useMemo(
    () => [
      t('addcase.input_placeholder'),
      t('addcase.input_placeholder_accountant'),
      t('addcase.input_placeholder_sales'),
      t('addcase.input_placeholder_hr'),
    ],
    [t]
  );

  useEffect(() => {
    if (!rotatingPlaceholders.length) return undefined;

    let timeoutId;
    let exampleIndex = 0;
    let characterIndex = 0;
    const typeSpeedMs = 38;
    const deleteSpeedMs = 20;
    const pauseAfterTypingMs = 1300;
    const pauseBeforeTypingMs = 250;

    const startTyping = () => {
      const fullText = rotatingPlaceholders[exampleIndex] || '';

      if (characterIndex < fullText.length) {
        characterIndex += 1;
        setTypedPlaceholder(fullText.slice(0, characterIndex));
        timeoutId = setTimeout(startTyping, typeSpeedMs);
        return;
      }

      timeoutId = setTimeout(startDeleting, pauseAfterTypingMs);
    };

    const startDeleting = () => {
      const fullText = rotatingPlaceholders[exampleIndex] || '';

      if (characterIndex > 0) {
        characterIndex -= 1;
        setTypedPlaceholder(fullText.slice(0, characterIndex));
        timeoutId = setTimeout(startDeleting, deleteSpeedMs);
        return;
      }

      exampleIndex = (exampleIndex + 1) % rotatingPlaceholders.length;
      timeoutId = setTimeout(startTyping, pauseBeforeTypingMs);
    };

    setTypedPlaceholder('');
    timeoutId = setTimeout(startTyping, pauseBeforeTypingMs);

    return () => clearTimeout(timeoutId);
  }, [rotatingPlaceholders]);

  useEffect(() => {
    let mounted = true;
    fetch('/api/server_status')
      .then(res => res.ok ? res.json() : Promise.reject())
      .then(data => { if (mounted && data && data.server_version) setServerVersion(data.server_version); })
      .catch(() => {});
    return () => { mounted = false; };
  }, []);
  
  const handleStartFromScratch = () => {
    setStatus('signup');
  };

  const handleUseTemplate = () => {
    setTemplateDialogOpen(true);
  };

  const handleManualCreation = () => {
    setManualCreationOpen(true);
  };

  const handleCloseManualCreation = () => {
    setManualCreationOpen(false);
  };

  const handleCreateFromManual = (projectData) => {
    shouldAwaitCreationUpdatesRef.current = Boolean(goal && goal.trim());
    try {
      sessionStorage.setItem(
        'dataunfold_case_creation_active',
        JSON.stringify({ startedAt: Date.now(), expiresAt: Date.now() + 15 * 60 * 1000 })
      );
    } catch (e) {
      // Ignore storage failures (e.g., private mode)
    }
    // Create project with manual data and AI processing
    sendMessage({
      command: 'AddProject',
      folder: "",
      title: projectData.title || "",
      description: projectData.description || "",
      goal,
      customFields: projectData.customFields || [],
      attachments: attachments,
      defer_ai_processing: true,

      projectModelProvider: projectTextModelProvider || projectModelProvider,
      project_text_model_id: projectTextModelId,
      project_text_model_provider: projectTextModelProvider || projectModelProvider,
      project_vision_model_id: projectVisionModelId,
      project_vision_model_provider: projectVisionModelProvider,
      project_audio_model_id: projectAudioModelId,
      project_audio_model_provider: projectAudioModelProvider,

    });
    setStatus('updating');
  };

  const handleAddProject = () => {
    shouldAwaitCreationUpdatesRef.current = Boolean(goal && goal.trim());
    try {
      sessionStorage.setItem(
        'dataunfold_case_creation_active',
        JSON.stringify({ startedAt: Date.now(), expiresAt: Date.now() + 15 * 60 * 1000 })
      );
    } catch (e) {
      // Ignore storage failures (e.g., private mode)
    }
    // Regular project creation with AI processing
    sendMessage({
      command: 'AddProject',
      folder: "",
      title: "",
      description: "",
      goal,
      customFields: [],
      attachments: attachments,
      defer_ai_processing: true,

      projectModelProvider: projectTextModelProvider || projectModelProvider,
      project_text_model_id: projectTextModelId,
      project_text_model_provider: projectTextModelProvider || projectModelProvider,
      project_vision_model_id: projectVisionModelId,
      project_vision_model_provider: projectVisionModelProvider,
      project_audio_model_id: projectAudioModelId,
      project_audio_model_provider: projectAudioModelProvider,

    });
    setStatus('updating');
  };

  const handleFileUpload = (event) => {
    const files = Array.from(event.target.files);
    
    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const newAttachment = {
          name: file.name,
          data: e.target.result, // This will be base64 encoded data
          size: file.size,
          type: file.type
        };
        setAttachments(prev => [...prev, newAttachment]);
      };
      reader.readAsDataURL(file);
    });
    
    // Reset the input so the same file can be selected again if needed
    event.target.value = '';
  };

  const removeAttachment = (index) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setDragOver(true);
  };

  const handleDragEnter = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setDragOver(true);
  };

  const handleDragLeave = (event) => {
    event.preventDefault();
    event.stopPropagation();
    // Only set dragOver to false if we're leaving the entire drag zone
    if (!event.currentTarget.contains(event.relatedTarget)) {
      setDragOver(false);
    }
  };

  const handleDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setDragOver(false);
    
    const files = Array.from(event.dataTransfer.files);
    
    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const newAttachment = {
          name: file.name,
          data: e.target.result, // This will be base64 encoded data
          size: file.size,
          type: file.type
        };
        setAttachments(prev => [...prev, newAttachment]);
      };
      reader.readAsDataURL(file);
    });
  };

  const handleCloseTemplateDialog = () => {
    setTemplateDialogOpen(false);
  };

  useEffect(() => {
    setGoal('');
    setProjectModelProvider('');

    setProjectTextModelId('');
    setProjectTextModelProvider('');
    setProjectVisionModelId('');
    setProjectVisionModelProvider('');
    setProjectAudioModelId('');
    setProjectAudioModelProvider('');
    setAttachments([]);
    setUpdateMessage('');
    setPendingCaseId(null);
    setStatus('signup'); // Show form by default
  }, []);
  
  const messageHandler = useCallback((parsedMessage) => {
    // parsedMessage is already parsed in WebSocketContext
    switch (parsedMessage.command) {
      case 'AddProject':
        if (parsedMessage.data.status === 'success') {
          const createdCaseId = parsedMessage.data.id;
          if (shouldAwaitCreationUpdatesRef.current) {
            setPendingCaseId(createdCaseId);
            setStatus('updating');
          } else {
            try {
              sessionStorage.removeItem('dataunfold_case_creation_active');
            } catch (e) {
              // Ignore storage failures (e.g., private mode)
            }
            navigate(`/case/${createdCaseId}`);
          }
        }
        break;
      case 'UpdateCreationCase':
        let creationActive = false;
        try {
          const raw = sessionStorage.getItem('dataunfold_case_creation_active');
          if (raw) {
            const parsed = JSON.parse(raw);
            const expiresAt = Number(parsed?.expiresAt || 0);
            if (Number.isFinite(expiresAt) && expiresAt > Date.now()) {
              creationActive = true;
            } else {
              sessionStorage.removeItem('dataunfold_case_creation_active');
            }
          }
        } catch (e) {
          // Ignore storage failures (e.g., private mode)
        }
        if (!creationActive) {
          break;
        }
        const updateMessage = parsedMessage.data.message;
        setStatus('updating');
        setUpdateMessage(updateMessage);
        break;
      case 'saved_models':
        setSavedModels(parsedMessage.data || { text: [], vision: [], audio: [] });
        break;
      case 'certification':
        // Certification messages may indicate software updates or invalid license
        if (parsedMessage.data && parsedMessage.data.server_version) {
          setServerVersion(parsedMessage.data.server_version);
        }
        break;
      case 'configuration_data':
        // configuration_data may include server version; pick it up if present
        if (parsedMessage.data && parsedMessage.data.server_version) {
          setServerVersion(parsedMessage.data.server_version);
        }
        break;
      default:
        break;
    }
  }, [navigate]);

  useEffect(() => {
    if (!pendingCaseId || !updateMessage) return;
    const normalized = String(updateMessage);
    const widgetPhaseMatch = /widget/i.test(normalized);
    const noWidgetsMatch = /(no widgets were suggested|no widgets were created|no data fields available for widget generation)/i.test(normalized);
    if (!widgetPhaseMatch) return;
    setPendingCaseId(null);
    if (noWidgetsMatch) {
      navigate(`/case/${pendingCaseId}`);
    } else {
      navigate(`/case/${pendingCaseId}?reviewWidgets=true`);
    }
  }, [pendingCaseId, updateMessage, navigate]);

  useEffect(() => {
    if (!pendingCaseId || updateMessage) return undefined;
    // Fallback for cases where no creation-progress messages are emitted.
    const timeoutId = setTimeout(() => {
      setPendingCaseId(null);
      try {
        sessionStorage.removeItem('dataunfold_case_creation_active');
      } catch (e) {
        // Ignore storage failures (e.g., private mode)
      }
      navigate(`/case/${pendingCaseId}`);
    }, 120000);
    return () => clearTimeout(timeoutId);
  }, [pendingCaseId, updateMessage, navigate]);

  useEffect(() => {
    const unsubscribe = subscribeToMessages(messageHandler);
    return unsubscribe;
  }, [subscribeToMessages, messageHandler]);

  useEffect(() => {
    sendMessage({ command: 'get_saved_models' });
  }, [sendMessage]);

  const handleModelSelect = (type) => (event) => {
    const value = event.target.value;
    const modelList = savedModels?.[type] || [];
    const selectedModel = modelList.find((model) => String(model.id) === String(value));
    const provider = value ? (selectedModel?.provider || '') : '';

    if (type === 'text') {
      setProjectTextModelId(value);
      setProjectTextModelProvider(provider);
    } else if (type === 'vision') {
      setProjectVisionModelId(value);
      setProjectVisionModelProvider(provider);
    } else if (type === 'audio') {
      setProjectAudioModelId(value);
      setProjectAudioModelProvider(provider);
    }
  };

  return (
    <Box 
      className="container" 
      sx={{ 
        backgroundColor: '#f7f7ff', 
        background: 'linear-gradient(135deg, #f7f7ff 0%, #ffffff 100%)',
        backgroundImage: 'linear-gradient(135deg, #f7f7ff 0%, #ffffff 70%, rgba(87, 87, 176, 0.08) 100%)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 4,
        position: 'relative'
      }}
    >
      <Box 
        sx={{ 
          maxWidth: '900px',
          width: '100%',
          mx: 'auto'
        }}
      >
        {status === 'initial' ? (
          // Initial step: Choose template or start from scratch
          <>
            <Typography 
              variant="h4" 
              component="h1" 
              gutterBottom
              sx={{
                color: primaryColor,
                fontWeight: 700,
                mb: 2,
                textAlign: 'center',
                textShadow: '0px 1px 2px rgba(87, 87, 176, 0.15)'
              }}
            >
              {t('addcase.welcome_title')}
            </Typography>

            <Typography 
              variant="h6" 
              component="p"
              sx={{
                color: 'text.secondary',
                mb: 4,
                textAlign: 'center',
                fontWeight: 400,
                lineHeight: 1.5
              }}
            >
              {t('addcase.choose_how_to_start')}
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3, mt: 4 }}>
              {/* Use Template Option */}
              <Card
                elevation={0}
                sx={{
                  flex: 1,
                  borderRadius: '16px',
                  p: 4,
                  cursor: 'pointer',
                  background: 'linear-gradient(135deg, #ffffff 0%, #fcfcff 100%)',
                  border: '2px solid rgba(87, 87, 176, 0.15)',
                  boxShadow: '0 6px 20px rgba(87, 87, 176, 0.08)',
                  transition: 'all 0.3s ease',
                  position: 'relative',
                  overflow: 'hidden',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: '0 12px 32px rgba(87, 87, 176, 0.15)',
                    borderColor: primaryColor,
                    '&::before': {
                      transform: 'translateX(100%)'
                    }
                  },
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: '-100%',
                    width: '100%',
                    height: '100%',
                    background: 'linear-gradient(90deg, transparent, rgba(87, 87, 176, 0.05), transparent)',
                    transition: 'transform 0.6s ease'
                  }
                }}
                onClick={handleUseTemplate}
              >
                <Box sx={{ textAlign: 'center', position: 'relative', zIndex: 1 }}>
                  <TemplateIcon 
                    sx={{ 
                      fontSize: 64,
                      color: primaryColor,
                      mb: 2,
                      filter: 'drop-shadow(0 4px 8px rgba(87, 87, 176, 0.2))'
                    }} 
                  />
                  <Typography 
                    variant="h5" 
                    sx={{
                      color: primaryColor,
                      fontWeight: 'bold',
                      mb: 2
                    }}
                  >
                    {t('addcase.use_template')}
                  </Typography>
                  <Typography 
                    variant="body1" 
                    sx={{
                      color: 'text.secondary',
                      lineHeight: 1.6,
                      mb: 2
                    }}
                  >
                    {t('addcase.template_description')}
                  </Typography>
                  <Box sx={{ mt: 3 }}>
                    <Typography 
                      variant="body2" 
                      sx={{
                        color: primaryColor,
                        fontWeight: 'medium',
                        textDecoration: 'underline',
                        textDecorationColor: 'transparent',
                        transition: 'text-decoration-color 0.3s ease'
                      }}
                    >
                      {t('addcase.browse_templates')} →
                    </Typography>
                  </Box>
                </Box>
              </Card>

              {/* Start from Scratch Option */}
              <Card
                elevation={0}
                sx={{
                  flex: 1,
                  borderRadius: '16px',
                  p: 4,
                  cursor: 'pointer',
                  background: 'linear-gradient(135deg, #ffffff 0%, #fcfcff 100%)',
                  border: '2px solid rgba(87, 87, 176, 0.15)',
                  boxShadow: '0 6px 20px rgba(87, 87, 176, 0.08)',
                  transition: 'all 0.3s ease',
                  position: 'relative',
                  overflow: 'hidden',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: '0 12px 32px rgba(87, 87, 176, 0.15)',
                    borderColor: primaryColor,
                    '&::before': {
                      transform: 'translateX(100%)'
                    }
                  },
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: '-100%',
                    width: '100%',
                    height: '100%',
                    background: 'linear-gradient(90deg, transparent, rgba(87, 87, 176, 0.05), transparent)',
                    transition: 'transform 0.6s ease'
                  }
                }}
                onClick={handleStartFromScratch}
              >
                <Box sx={{ textAlign: 'center', position: 'relative', zIndex: 1 }}>
                  <CreateNewFolderIcon 
                    sx={{ 
                      fontSize: 64,
                      color: primaryColor,
                      mb: 2,
                      filter: 'drop-shadow(0 4px 8px rgba(87, 87, 176, 0.2))'
                    }} 
                  />
                  <Typography 
                    variant="h5" 
                    sx={{
                      color: primaryColor,
                      fontWeight: 'bold',
                      mb: 2
                    }}
                  >
                    {t('addcase.start_from_scratch')}
                  </Typography>
                  <Typography 
                    variant="body1" 
                    sx={{
                      color: 'text.secondary',
                      lineHeight: 1.6,
                      mb: 2
                    }}
                  >
                    {t('addcase.scratch_description')}
                  </Typography>
                  <Box sx={{ mt: 3 }}>
                    <Typography 
                      variant="body2" 
                      sx={{
                        color: primaryColor,
                        fontWeight: 'medium',
                        textDecoration: 'underline',
                        textDecorationColor: 'transparent',
                        transition: 'text-decoration-color 0.3s ease'
                      }}
                    >
                      {t('addcase.create_new_project')} →
                    </Typography>
                  </Box>
                </Box>
              </Card>
            </Box>

            {/* Optional: Add a note about what happens next */}
            <Box 
              sx={{ 
                mt: 4, 
                p: 2, 
                borderRadius: '12px', 
                backgroundColor: 'rgba(87, 87, 176, 0.05)',
                border: '1px solid rgba(87, 87, 176, 0.1)'
              }}
            >
              <Typography 
                variant="body2" 
                sx={{
                  color: 'text.secondary',
                  textAlign: 'center',
                  fontStyle: 'italic'
                }}
              >
                {t('addcase.selection_note')}
              </Typography>
            </Box>
          </>
        ) : status === 'signup' ? (
          <>
            {/* Floating buttons - top right */}
            <Box sx={{ 
              position: 'fixed', 
              top: { xs: 16, sm: 24 }, 
              right: { xs: 16, sm: 24 }, 
              zIndex: 1000,
              display: 'flex', 
              flexDirection: { xs: 'column', sm: 'row' },
              gap: { xs: 1, sm: 1.5 }
            }}>
              <Button
                onClick={handleManualCreation}
                variant="outlined"
                startIcon={<InfoIcon sx={{ fontSize: { xs: '18px', sm: '20px' } }} />}
                sx={{
                  color: 'rgba(87, 87, 176, 0.7)',
                  borderColor: 'rgba(87, 87, 176, 0.3)',
                  borderRadius: '8px',
                  px: { xs: 1.5, sm: 2 },
                  py: { xs: 0.75, sm: 1 },
                  textTransform: 'none',
                  fontWeight: 500,
                  fontSize: { xs: '0.813rem', sm: '0.875rem' },
                  minWidth: { xs: 'auto', sm: 'auto' },
                  backgroundColor: 'rgba(255, 255, 255, 0.95)',
                  backdropFilter: 'blur(10px)',
                  boxShadow: '0 2px 8px rgba(87, 87, 176, 0.15)',
                  '&:hover': {
                    backgroundColor: 'rgba(87, 87, 176, 0.1)',
                    borderColor: primaryColor,
                    boxShadow: '0 4px 12px rgba(87, 87, 176, 0.25)'
                  }
                }}
              >
                {t('addcase.manual_creation_button')}
              </Button>
              <Button
                onClick={handleUseTemplate}
                variant="outlined"
                startIcon={<TemplateIcon sx={{ fontSize: { xs: '18px', sm: '20px' } }} />}
                sx={{
                  color: primaryColor,
                  borderColor: 'rgba(87, 87, 176, 0.3)',
                  borderRadius: '8px',
                  px: { xs: 1.5, sm: 2 },
                  py: { xs: 0.75, sm: 1 },
                  textTransform: 'none',
                  fontWeight: 500,
                  fontSize: { xs: '0.813rem', sm: '0.875rem' },
                  minWidth: { xs: 'auto', sm: 'auto' },
                  backgroundColor: 'rgba(255, 255, 255, 0.95)',
                  backdropFilter: 'blur(10px)',
                  boxShadow: '0 2px 8px rgba(87, 87, 176, 0.15)',
                  '&:hover': {
                    backgroundColor: 'rgba(87, 87, 176, 0.1)',
                    borderColor: primaryColor,
                    boxShadow: '0 4px 12px rgba(87, 87, 176, 0.25)'
                  }
                }}
              >
                {t('addcase.use_template')}
              </Button>
            </Box>

            {/* Main Content - Simple and Centered */}
            <Box 
              sx={{ 
                textAlign: 'center',
                mt: 8,
                mb: 6,
                position: 'relative',
                transition: 'all 0.3s ease',
                '&::before': dragOver ? {
                  content: '"📎 Drop files to attach"',
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  fontSize: '1.5rem',
                  fontWeight: 600,
                  color: primaryColor,
                  backgroundColor: 'rgba(255, 255, 255, 0.98)',
                  padding: '24px 48px',
                  borderRadius: '16px',
                  border: `3px dashed ${primaryColor}`,
                  zIndex: 1000,
                  pointerEvents: 'none',
                  boxShadow: '0 8px 32px rgba(87, 87, 176, 0.3)'
                } : {}
              }}
              onDragOver={handleDragOver}
              onDragEnter={handleDragEnter}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              {/* Logo Section - Centered above the title */}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 4,
                  mt: { xs: 2, sm: 4 },
                }}
              >
                <Box
                  sx={{
                    position: 'relative',
                    mb: 2,
                    animation: 'fadeInUp 1s ease-out',
                  }}
                >
                  <Box
                    sx={{
                      position: 'absolute',
                      inset: '-20px',
                      borderRadius: '50%',
                      background: `radial-gradient(circle, ${primaryColorTransparent} 0%, transparent 70%)`,
                      animation: 'pulse 3s infinite ease-in-out',
                      opacity: 0.4,
                    }}
                  />
                  <img
                    src={logo}
                    alt="DataUnfold Logo"
                    style={{
                      width: '100px',
                      height: '100px',
                      animation: 'glow 4s infinite ease-in-out, float 8s infinite ease-in-out',
                      position: 'relative',
                      zIndex: 2,
                      filter: `drop-shadow(0 0 20px ${primaryColorTransparent})`,
                    }}
                  />
                </Box>
              </Box>

              <Typography 
                variant="h4" 
                component="h1"
                sx={{
                  color: primaryColor,
                  fontWeight: 600,
                  mb: 4,
                  fontSize: '2rem'
                }}
              >
                {t('addcase.where_should_we_begin')}
              </Typography>

              <TextField
                variant="outlined"
                fullWidth
                multiline
                rows={3}
                value={goal}
                onChange={(e) => setGoal(e.target.value.slice(0, 200))}
                placeholder={typedPlaceholder || t('addcase.input_placeholder')}
                inputProps={{ maxLength: 200 }}
                helperText={t('addcase.characters_left', { remaining: Math.max(0, 200 - goal.length) })}
                FormHelperTextProps={{
                  sx: {
                    textAlign: 'right',
                    maxWidth: '700px',
                    mx: 'auto',
                    color: 'rgba(87, 87, 176, 0.7)',
                  },
                }}
                sx={{
                  mb: 2,
                  maxWidth: '700px',
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '10px',
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    paddingRight: '14px',
                    '& .MuiInputBase-inputMultiline': {
                      fontSize: '1.3rem',
                      lineHeight: 1.6,
                    },
                    '& .MuiInputBase-input::placeholder': {
                      fontSize: '1.2rem',
                      color: 'rgba(0, 0, 0, 0.38)',
                      opacity: 1,
                    },
                    '&:hover fieldset': {
                      borderColor: hoverColor,
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: primaryColor,
                      borderWidth: '2px'
                    }
                  },
                  '& .MuiInputLabel-outlined.Mui-focused': {
                    color: primaryColor,
                  }
                }}
                InputProps={{
                  endAdornment: (
                    <Tooltip title={t('addcase.reference_documents_placeholder')} arrow>
                      <IconButton
                        component="label"
                        size="small"
                        sx={{
                          color: dragOver ? hoverColor : (attachments.length > 0 ? primaryColor : 'rgba(87, 87, 176, 0.5)'),
                          '&:hover': {
                            color: primaryColor,
                            backgroundColor: 'rgba(87, 87, 176, 0.1)',
                          }
                        }}
                      >
                        <AttachFileIcon fontSize="small" />
                        <input
                          accept="*/*"
                          style={{ display: 'none' }}
                          multiple
                          type="file"
                          onChange={handleFileUpload}
                        />
                      </IconButton>
                    </Tooltip>
                  )
                }}
              />

              {/* Attached Files List - Compact */}
              {attachments.length > 0 && (
                <Box sx={{ mb: 1.5, maxWidth: '700px', mx: 'auto' }}>
                  <Typography variant="caption" sx={{ mb: 1, color: primaryColor, fontWeight: 600, display: 'block', fontSize: '0.75rem', textAlign: 'left' }}>
                    {t('addcase.attached_files')} ({attachments.length})
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {attachments.map((file, index) => (
                      <Chip
                        key={index}
                        label={`${file.name} (${(file.size / 1024).toFixed(1)} KB)`}
                        onDelete={() => removeAttachment(index)}
                        deleteIcon={<DeleteIcon />}
                        size="small"
                        sx={{
                          maxWidth: '200px',
                          height: '24px',
                          backgroundColor: 'rgba(255, 255, 255, 0.9)',
                          border: '1px solid rgba(87, 87, 176, 0.2)',
                          '& .MuiChip-label': {
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            fontSize: '0.7rem',
                            padding: '0 6px'
                          },
                          '& .MuiChip-deleteIcon': {
                            fontSize: '0.8rem',
                            margin: '0 2px 0 0'
                          },
                          '&:hover': {
                            backgroundColor: 'rgba(255, 255, 255, 1)',
                            transform: 'scale(1.02)',
                          }
                        }}
                      />
                    ))}
                  </Box>
                </Box>
              )}

              {/* Create Analysis Button */}
              <Button
                variant="contained"
                startIcon={<CreateNewFolderIcon />}
                onClick={handleAddProject}
                disabled={!goal.trim()}
                sx={{
                  px: 5,
                  py: 1.5,
                  fontSize: '1rem',
                  borderRadius: '50px',
                  boxShadow: '0 4px 14px rgba(87, 87, 176, 0.25)',
                  fontWeight: 600,
                  background: !goal.trim()
                    ? 'linear-gradient(to right, #e0e0e0, #f5f5f5)'
                    : `linear-gradient(to right, ${primaryColor}, ${hoverColor})`,
                  color: !goal.trim() ? '#b0b0b0' : '#fff',
                  opacity: !goal.trim() ? 0.7 : 1,
                  cursor: !goal.trim() ? 'not-allowed' : 'pointer',
                  textTransform: 'none',
                  transition: 'all 0.3s ease',
                  mt: 2,
                  '&:hover': !goal.trim()
                    ? {
                        background: 'linear-gradient(to right, #e0e0e0, #f5f5f5)',
                        boxShadow: 'none',
                        transform: 'none',
                      }
                    : {
                        transform: 'translateY(-2px)',
                        boxShadow: '0 6px 20px rgba(87, 87, 176, 0.35)',
                        background: `linear-gradient(to right, ${primaryColor}, ${selectedColor})`,
                      }
                }}
              >
                {t('addcase.create_analysis')}
              </Button>

            </Box>
          </>
        ) : (
          <LoadingScreen status={status} updateMessage={updateMessage} />
        )}
      </Box>

      {/* Server version footer */}
      <Box sx={{
        position: 'absolute',
        bottom: 8,
        left: 0,
        right: 0,
        textAlign: 'center',
        zIndex: 1,
      }}>
        <Typography variant="caption" sx={{ color: 'rgba(0,0,0,0.6)' }}>
          {serverVersion ? `V: ${serverVersion}` : 'Server: unknown'}
        </Typography>
      </Box>

      {/* Manual Creation Dialog */}
      <ManualCreationDialog
        open={manualCreationOpen}
        onClose={handleCloseManualCreation}
        onCreateProject={handleCreateFromManual}
      />

      {/* Template Selection Dialog */}
      <TemplateSelectionDialog
        open={templateDialogOpen}
        onClose={handleCloseTemplateDialog}
        onSelectTemplate={() => {}}
      />
    </Box>
  );
};

export default AddProject;
